/**
 * Module configurations for the 4 distinct app modules.
 * Each module has its own brand identity, theme colors, and navigation.
 */
import {
  Home, ShoppingBag, Wallet, Network, User, MessageCircle, Trophy, Truck, ShieldAlert,
  Store, Package, FileText, BarChart3, Settings, Users, DollarSign,
  Banknote, Ticket, Mail, Rocket, Navigation, MapPin, MessageSquare, LifeBuoy, Tag, Crown, Sparkles, Receipt, Siren, Megaphone, Shield, CircleUser, ShieldCheck, Target, UserPlus, Medal, Handshake, ScanFace
} from "lucide-react";

export const moduleConfigs = {
  cliente: {
    key: "cliente",
    name: "GoFood",
    role: "Cliente",
    description: "Peça suas comidas favoritas",
    // Warm food-friendly palette: orange & cream
    theme: {
      bg: "bg-[#FFF7ED]",          // warm cream
      sidebar: "bg-white",
      headerBg: "bg-white",
      brand: "text-orange-600",
      accent: "bg-orange-600 hover:bg-orange-700",
      accentText: "text-orange-600",
      ring: "ring-orange-500",
      dot: "bg-orange-500",
    },
    nav: [
      { label: "Início", to: "/cliente/home", icon: Home, testId: "nav-home" },
      { label: "Pedidos", to: "/cliente/pedidos", icon: ShoppingBag, testId: "nav-orders" },
      { label: "Carteira", to: "/cliente/carteira", icon: Wallet, testId: "nav-wallet" },
      { label: "Rede", to: "/cliente/rede", icon: Network, testId: "nav-network" },
      { label: "Conversas", to: "/cliente/conversas", icon: MessageCircle, testId: "nav-chats", chatBadge: true },
      { label: "Bolão da Copa", to: "/bolao", icon: Medal, testId: "nav-bolao" },
      { label: "Suporte", to: "/suporte", icon: LifeBuoy, testId: "nav-support" },
      { label: "Ranking", to: "/cliente/ranking", icon: Trophy, testId: "nav-leaderboard" },
      { label: "Perfil", to: "/cliente/perfil", icon: User, testId: "nav-profile" },
    ],
  },
  loja: {
    key: "loja",
    name: "GoFood",
    role: "Loja",
    description: "Painel do estabelecimento",
    // Business / professional: deep emerald & cream
    theme: {
      bg: "bg-emerald-50",
      sidebar: "bg-emerald-900",
      sidebarText: "text-emerald-50",
      headerBg: "bg-white",
      brand: "text-emerald-700",
      accent: "bg-emerald-700 hover:bg-emerald-800",
      accentText: "text-emerald-700",
      ring: "ring-emerald-500",
      dot: "bg-emerald-500",
      isDarkSidebar: true,
    },
    nav: [
      { label: "Painel", to: "/loja/painel", icon: Home, testId: "nav-home" },
      { label: "Pedidos", to: "/loja/pedidos", icon: Package, testId: "nav-orders" },
      { label: "Faturas", to: "/loja/faturas", icon: FileText, testId: "nav-billing" },
      { label: "Carteira", to: "/loja/carteira", icon: Wallet, testId: "nav-wallet" },
      { label: "Conversas", to: "/loja/conversas", icon: MessageCircle, testId: "nav-chats", chatBadge: true },
      { label: "Franquia", to: "/lojista/chat", icon: MessageCircle, testId: "nav-franchise-chat" },
      { label: "Suporte", to: "/suporte", icon: LifeBuoy, testId: "nav-support" },
      { label: "Rede", to: "/loja/rede", icon: Network, testId: "nav-network" },
      { label: "Antecedentes", to: "/verificacao", icon: ShieldCheck, testId: "nav-bgcheck" },
      { label: "Perfil", to: "/loja/perfil", icon: User, testId: "nav-profile" },
    ],
  },
  entregador: {
    key: "entregador",
    name: "GoFood",
    role: "Entregador",
    description: "Pegue suas entregas",
    // High-energy mobile: black + electric yellow (Uber Driver vibe)
    theme: {
      bg: "bg-zinc-950",
      sidebar: "bg-black",
      sidebarText: "text-yellow-50",
      headerBg: "bg-black",
      brand: "text-yellow-400",
      accent: "bg-yellow-400 hover:bg-yellow-500 text-black",
      accentText: "text-yellow-400",
      ring: "ring-yellow-400",
      dot: "bg-yellow-400",
      isDarkSidebar: true,
      isDarkMode: true,
    },
    nav: [
      { label: "Entregas", to: "/entregador/entregas", icon: Truck, testId: "nav-home" },
      { label: "Mapa", to: "/entregador/mapa", icon: MapPin, testId: "nav-map" },
      { label: "Carteira", to: "/entregador/carteira", icon: Wallet, testId: "nav-wallet" },
      { label: "Bolão da Copa", to: "/bolao", icon: Medal, testId: "nav-bolao" },
      { label: "Suporte", to: "/suporte", icon: LifeBuoy, testId: "nav-support" },
      { label: "Rede", to: "/entregador/rede", icon: Network, testId: "nav-network" },
      { label: "Antecedentes", to: "/verificacao", icon: ShieldCheck, testId: "nav-bgcheck" },
      { label: "Perfil", to: "/entregador/perfil", icon: User, testId: "nav-profile" },
    ],
  },
  admin: {
    key: "admin",
    name: "GoFood",
    role: "Admin",
    description: "Painel administrativo",
    // Data-dense neutral: slate / zinc
    theme: {
      bg: "bg-slate-50",
      sidebar: "bg-slate-900",
      sidebarText: "text-slate-50",
      headerBg: "bg-white",
      brand: "text-slate-800",
      accent: "bg-slate-800 hover:bg-slate-900",
      accentText: "text-slate-800",
      ring: "ring-slate-500",
      dot: "bg-slate-500",
      isDarkSidebar: true,
    },
    nav: [
      { label: "Dashboard", to: "/admin/painel", icon: Home, testId: "nav-home" },
      { label: "Rede MLM", to: "/admin/network", icon: Network, testId: "nav-network" },
      { label: "Usuários", to: "/admin/users", icon: Users, testId: "nav-users" },
      { label: "Lojas", to: "/admin/merchants", icon: Store, testId: "nav-merchants" },
      { label: "Motoboys", to: "/admin/drivers", icon: Truck, testId: "nav-drivers" },
      { label: "SOS Emergência", to: "/admin/sos", icon: Siren, testId: "nav-sos" },
      { label: "Antecedentes", to: "/admin/background-checks", icon: ShieldCheck, testId: "nav-background-checks" },
      { label: "Auditoria", to: "/admin/auditoria", icon: Shield, testId: "nav-audit" },
      { label: "Campanhas Push", to: "/admin/campaigns", icon: Megaphone, testId: "nav-campaigns" },
      { label: "Publicidade", to: "/admin/publicidade", icon: Target, testId: "nav-ads" },
      { label: "Recrutamento", to: "/admin/recrutamento", icon: UserPlus, testId: "nav-recruitment" },
      { label: "Validação CNH", to: "/admin/cnh-validations", icon: ShieldCheck, testId: "nav-cnh-validations" },
      { label: "Facial", to: "/admin/face-verifications", icon: ScanFace, testId: "nav-face" },
      { label: "Prova de Vida", to: "/admin/identity-review", icon: ShieldCheck, testId: "nav-identity" },
      { label: "Bolão da Copa", to: "/admin/bolao", icon: Medal, testId: "nav-bolao" },
      { label: "Sócios", to: "/admin/socios", icon: Handshake, testId: "nav-socios" },
      { label: "Chat In-App", to: "/admin/chat", icon: MessageCircle, testId: "nav-chat", chatBadge: true },
      { label: "Financeiro", to: "/admin/financial", icon: BarChart3, testId: "nav-financial" },
      { label: "Faturamento", to: "/admin/merchants/billing", icon: FileText, testId: "nav-billing" },
      { label: "Saques", to: "/admin/withdrawals", icon: Banknote, testId: "nav-withdrawals" },
      { label: "Suporte", to: "/admin/support", icon: MessageSquare, testId: "nav-support" },
      { label: "Cupons", to: "/admin/coupons", icon: Ticket, testId: "nav-coupons" },
      { label: "Banners", to: "/admin/banners", icon: Sparkles, testId: "nav-banners" },
      { label: "Cidades", to: "/admin/cities", icon: MapPin, testId: "nav-cities" },
      { label: "Categorias", to: "/admin/niches", icon: Tag, testId: "nav-niches" },
      { label: "Taxas", to: "/admin/rates", icon: DollarSign, testId: "nav-rates" },
      { label: "Integrações", to: "/admin/integrations", icon: Rocket, testId: "nav-integrations" },
      { label: "Vouchers", to: "/admin/vouchers", icon: Ticket, testId: "nav-vouchers" },
      { label: "Franquias", to: "/admin/franchises", icon: Rocket, testId: "nav-franchises" },
      { label: "Fiscal / NF-e", to: "/admin/fiscal", icon: Receipt, testId: "nav-fiscal" },
      { label: "Matriz Nacional", to: "/admin/hierarchy", icon: Crown, testId: "nav-hierarchy" },
      { label: "Lojinha GoFood", to: "/admin/lojinha", icon: ShoppingBag, testId: "nav-shop" },
      { label: "Centro de Segurança", to: "/admin/seguranca", icon: ShieldAlert, testId: "nav-safety" },
      { label: "Emails", to: "/admin/emails", icon: Mail, testId: "nav-emails" },
      { label: "Grupos WhatsApp", to: "/admin/grupos", icon: Users, testId: "nav-grupos" },
      { label: "Configurações", to: "/admin/settings", icon: Settings, testId: "nav-settings" },
      { label: "Meu Perfil", to: "/admin/perfil", icon: CircleUser, testId: "nav-profile" },
    ],
  },
  franquia: {
    key: "franquia",
    name: "GoFood",
    role: "Franqueado",
    description: "Painel da franquia",
    theme: {
      bg: "bg-indigo-50",
      sidebar: "bg-indigo-900",
      sidebarText: "text-indigo-50",
      headerBg: "bg-white",
      brand: "text-indigo-700",
      accent: "bg-indigo-700 hover:bg-indigo-800",
      accentText: "text-indigo-700",
      ring: "ring-indigo-500",
      dot: "bg-indigo-500",
      isDarkSidebar: true,
    },
    nav: [
      { label: "Painel", to: "/franquia/painel", icon: Home, testId: "nav-home" },
      { label: "Perfil", to: "/cliente/perfil", icon: User, testId: "nav-profile" },
    ],
  },
};

/**
 * Map user_type → module key
 */
export const getModuleForUser = (user) => {
  if (!user) return null;
  if (user.user_type === "admin" || user.email === "admin@gofoodbrasil.com.br" || user.email === "admin@gofood.com.br") return "admin";
  if (user.user_type === "master_franchise_admin") return "master";
  if (user.user_type === "franchise_admin") return "franchise";
  if (user.user_type === "franchisee") return "franquia";
  if (user.user_type === "merchant") return "loja";
  if (user.user_type === "driver") return "entregador";
  return "cliente";
};

/**
 * Default home route per module
 */
export const getModuleHomeRoute = (moduleKey) => {
  const routes = {
    cliente: "/cliente/home",
    loja: "/loja/painel",
    entregador: "/entregador/entregas",
    admin: "/admin/painel",
    franquia: "/franquia/painel",
    master: "/master/dashboard",
    franchise: "/franquia-regional/dashboard",
  };
  return routes[moduleKey] || "/cliente/home";
};
